import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Card, Button } from '@/components/ui';
import { useDeviceSize } from '../hooks/useDeviceSize';

interface FiltersCardProps {
  title?: string;
  subtitle?: string;
  /** When false, subtitle is hidden (e.g. tablet compact mode). */
  showSubtitle?: boolean;
  children: ReactNode;
  className?: string;
  /** Applied to the card body wrapper (e.g. scrollable region on tablet). */
  bodyClassName?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /**
   * Start collapsed on a phone (< md), regardless of `defaultCollapsed`.
   *
   * Measured on a 414 x 896 viewport: an expanded filter card pushed the first row of data to
   * y=1153 on Work IDs, y=964 on Quantified and y=740 on Routine — so the user scrolled past a
   * screenful of filters before seeing a single record. That is the "a lot of top nav items and
   * filters, I cannot see items at all" report. Every caller passes `defaultCollapsed={false}`,
   * which was written with a desktop in mind, so this deliberately overrides it on phones only.
   *
   * Requires `collapsible` — there is no point starting collapsed with no way to expand.
   */
  collapseOnPhone?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  
  applyButtonText?: string;
  showClearButton?: boolean;
  onClear?: () => void;
  clearButtonText?: string;
  hasActiveFilters?: boolean;
}

const FiltersCard: React.FC<FiltersCardProps> = ({
  title = 'Filters',
  subtitle,
  showSubtitle = true,
  children,
  className = '',
  bodyClassName = '',
  collapsible = false,
  defaultCollapsed = false,
  collapseOnPhone = true,
  onToggleCollapse,
  showApplyButton = false,
  onApply,
  applyDisabled = false,
  applyButtonText = 'Apply',
  showClearButton = false,
  onClear,
  clearButtonText = 'Clear All',
  hasActiveFilters = false
}) => {
  const { isMobile } = useDeviceSize();
  const startCollapsedOnPhone = collapsible && collapseOnPhone;

  const [isCollapsed, setIsCollapsed] = useState(
    () => defaultCollapsed || (startCollapsedOnPhone && isMobile)
  );
  const previousDefaultCollapsed = useRef(defaultCollapsed);
  const wasMobile = useRef(isMobile);

  useEffect(() => {
    if (previousDefaultCollapsed.current !== defaultCollapsed) {
      setIsCollapsed(defaultCollapsed);
      previousDefaultCollapsed.current = defaultCollapsed;
    }
  }, [defaultCollapsed]);

  // Collapse when the viewport crosses INTO phone width (rotating a tablet to portrait, or a
  // desktop window being narrowed). Only on the crossing, so a deliberate expand is not stomped
  // on every resize event; and never on the way back out, where there is room to show them.
  useEffect(() => {
    if (isMobile && !wasMobile.current && startCollapsedOnPhone) {
      setIsCollapsed(true);
    }
    wasMobile.current = isMobile;
  }, [isMobile, startCollapsedOnPhone]);

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onToggleCollapse?.(newCollapsed);
  };

  const TitleTag = collapsible ? 'button' : 'div';

  return (
    <Card className={`tw-mb-3 ${className}`}>
      <Card.Header className="tw-py-2.5">
        {/* flex-wrap + a full-width title row below sm. With Clear All, Apply and the collapse
            toggle all in this header, three buttons plus their gaps consume ~300px of a 414px
            phone — measured, the title block was squeezed to ZERO width, so "Project Filters"
            vanished and the amber Active pill floated where the title should have been ("the
            active badge on the filter looks off"). Letting the buttons wrap to their own line
            gives the title its width back; above sm it is a single row exactly as before. */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-2 tw-gap-x-3">
          <TitleTag
            type={collapsible ? 'button' : undefined}
            onClick={collapsible ? handleToggleCollapse : undefined}
            className={`tw-flex tw-min-w-0 tw-basis-full tw-items-center tw-gap-2.5 tw-bg-transparent tw-p-0 tw-text-left sm:tw-basis-0 sm:tw-grow ${
              collapsible ? 'tw-cursor-pointer' : ''
            }`}
            title={collapsible ? (isCollapsed ? 'Expand filters' : 'Collapse filters') : undefined}
          >
            <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-surface-alt tw-text-content-secondary">
              <i className="bi bi-funnel" aria-hidden="true"></i>
            </span>
            <span className="tw-min-w-0">
              <span className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-content">{title}</span>
                {hasActiveFilters && (
                  <span
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-accent/15 tw-px-2 tw-py-0.5 tw-text-[0.65rem] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accent"
                    title="Filters are active"
                  >
                    <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-accent"></span>
                    Active
                  </span>
                )}
              </span>
              {subtitle && showSubtitle && (
                <span className="tw-mt-0.5 tw-block tw-truncate tw-text-xs tw-text-content-muted">{subtitle}</span>
              )}
            </span>
          </TitleTag>
          <div className="tw-flex tw-shrink-0 tw-grow tw-items-center tw-justify-end tw-gap-2 sm:tw-grow-0">
            {/* Action buttons */}
            {showClearButton && hasActiveFilters && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={onClear}
                title="Clear all filters"
              >
                <i className="bi bi-x-circle tw-mr-1"></i>
                {clearButtonText}
              </Button>
            )}
            {showApplyButton && (
              <Button
                variant="primary"
                size="sm"
                onClick={onApply}
                disabled={applyDisabled}
                title="Apply filters"
              >
                <i className="bi bi-search tw-mr-1"></i>
                {applyButtonText}
              </Button>
            )}
            {/* Collapse button */}
            {collapsible && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleToggleCollapse}
                title={isCollapsed ? "Expand filters" : "Collapse filters"}
              >
                <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
              </Button>
            )}
          </div>
        </div>
      </Card.Header>
      {(!collapsible || !isCollapsed) && (
        <Card.Body className={bodyClassName}>
          {children}
        </Card.Body>
      )}
    </Card>
  );
};

export default FiltersCard; 